import { useState, useEffect } from 'react';

// Breakpoints que coinciden con los de Tailwind
const BREAKPOINTS = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
  '3xl': 1920,
  fhd: 1920,
  qhd: 2560,
  uhd: 3840
};

export const useResponsive = () => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { width, height } = dimensions;

  // Detectar orientación
  const isPortrait = height > width;
  const isLandscape = width > height;

  // Detectar breakpoint actual
  const getCurrentBreakpoint = () => {
    if (width >= BREAKPOINTS.uhd) return 'uhd';
    if (width >= BREAKPOINTS.qhd) return 'qhd';
    if (width >= BREAKPOINTS.fhd) return 'fhd';
    if (width >= BREAKPOINTS['2xl']) return '2xl';
    if (width >= BREAKPOINTS.xl) return 'xl';
    if (width >= BREAKPOINTS.lg) return 'lg';
    if (width >= BREAKPOINTS.md) return 'md';
    if (width >= BREAKPOINTS.sm) return 'sm';
    if (width >= BREAKPOINTS.xs) return 'xs';
    return 'xs';
  };

  // Calcular escala basada en el diseño original (1920x1080)
  const getScale = () => {
    const baseWidth = 1920;
    const baseHeight = 1080;
    
    if (isPortrait) {
      // En portrait, escalar basado en el ancho
      return Math.min(width / baseHeight, height / baseWidth);
    } else {
      // En landscape, escalar basado en ambas dimensiones
      return Math.min(width / baseWidth, height / baseHeight);
    }
  };

  // Funciones de utilidad para detectar tamaños específicos
  const isMobile = width < BREAKPOINTS.md;
  const isTablet = width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;
  const isDesktop = width >= BREAKPOINTS.lg;
  const isLargeScreen = width >= BREAKPOINTS['2xl'];
  const isOriginalSize = width >= BREAKPOINTS.fhd && height >= 1080;

  // Función para obtener clases CSS responsivas
  const getResponsiveClasses = (mobileClasses, tabletClasses, desktopClasses, largeClasses) => {
    if (isLargeScreen && largeClasses) return largeClasses;
    if (isDesktop && desktopClasses) return desktopClasses;
    if (isTablet && tabletClasses) return tabletClasses;
    return mobileClasses;
  };

  // Función para obtener valores responsivos
  const getResponsiveValue = (values) => {
    const breakpoint = getCurrentBreakpoint();
    return values[breakpoint] || values.default || values.xs;
  };

  return {
    width,
    height,
    isPortrait,
    isLandscape,
    isMobile,
    isTablet,
    isDesktop,
    isLargeScreen,
    isOriginalSize,
    breakpoint: getCurrentBreakpoint(),
    scale: getScale(),
    getResponsiveClasses,
    getResponsiveValue
  };
};

export default useResponsive;